import {
    Address,
    CheckoutChargeStatusType,
    ProviderGuarantor,
    ProviderPaymentPlan,
    Spinner,
    SupportCheckoutCharge,
    SupportCreditCard,
    ToastContext,
} from '@enter/core';
import { format } from 'date-fns';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useState } from 'react';
import { RootStore } from 'stores';
import * as S from './guarantor-overview-card.styles';

interface IGuarantorOverviewCardProps {
    loading: boolean;
    claimId: string;
    guarantor: ProviderGuarantor | undefined;
    checkoutCharges: SupportCheckoutCharge[] | undefined;
    creditCards: SupportCreditCard[] | undefined;
    paymentPlans: ProviderPaymentPlan[] | undefined;
}

// NB: show all credit cards; TODO: cross-reference Transaction.chargedBankingId
export const GuarantorOverviewCard: React.FunctionComponent<IGuarantorOverviewCardProps> = inject('rootStore')(
    observer(
        (props: IGuarantorOverviewCardProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { loading, guarantor, checkoutCharges, creditCards, paymentPlans } = props;

            const toastContext = useContext(ToastContext);
            const [isWaiting, setWaiting] = useState(false);

            const refundCharge = (checkoutCharge: SupportCheckoutCharge): (() => Promise<void>) => {
                return async (): Promise<void> => {
                    await claimStore.refundCheckoutCharge(checkoutCharge, null as any);
                };
            };
            const viewCurrentBillingDocument = async (): Promise<void> => {
                try {
                    if (guarantor && guarantor.id) {
                        setWaiting(true);
                        const tempUrl = await claimStore.getGuarantorViewBillUrl(guarantor.id);
                        setWaiting(false);
                        if (tempUrl && tempUrl.url) {
                            window.open(tempUrl.url, '_blank');
                        }
                    }
                } catch (err) {
                    toastContext.notify?.('Billing document is currently unavailable.', 'right');
                }
            };
            const renderLoader = (): React.ReactElement => (
                <S.Loader>
                    <Spinner />
                </S.Loader>
            );
            const renderAddress = (address: Address, i: number): React.ReactElement => (
                <tr key={`address-${i}`}>
                    <S.TH align="right">Address</S.TH>
                    <S.TD>
                        {address.lines.join('\n')}
                        <br />
                        {address.city}, {address.state} {address.postal}
                    </S.TD>
                </tr>
            );
            const renderGuarantor = (): React.ReactElement | undefined => {
                if (guarantor) {
                    const { first, middle, last, suffix, organization, dob, phone, email, addresses } = guarantor;
                    const addressItems = _.map(addresses, (address, i) => renderAddress(address, i));
                    return (
                        <S.Table>
                            <tbody>
                                <tr>
                                    <S.TH align="right">Name</S.TH>
                                    <S.TD colSpan={2}>
                                        {first} {middle} {last} {suffix}
                                    </S.TD>
                                </tr>
                                <tr>
                                    <S.TH align="right">Organization</S.TH>
                                    <S.TD colSpan={2}>{organization}</S.TD>
                                </tr>
                                <tr>
                                    <S.TH align="right">DOB</S.TH>
                                    <S.TD colSpan={2}>{format(dob || '', 'MM/dd/yyyy')}</S.TD>
                                </tr>
                                <tr>
                                    <S.TH align="right">Phone</S.TH>
                                    <S.TD colSpan={2}>{phone}</S.TD>
                                </tr>
                                <tr>
                                    <S.TH align="right">Email</S.TH>
                                    <S.TD colSpan={2}>{email}</S.TD>
                                </tr>
                                {addressItems}
                                <tr>
                                    <S.TH />
                                    <S.TD>
                                        <S.StyledButton
                                            variant="text"
                                            disabled={isWaiting}
                                            onClick={viewCurrentBillingDocument}>
                                            {isWaiting ? <S.WaitSpinner size="1em" /> : 'View current bill'}
                                        </S.StyledButton>
                                    </S.TD>
                                </tr>
                            </tbody>
                        </S.Table>
                    );
                }
            };
            const renderPayments = (): React.ReactElement => {
                const creditCardRows = _.map(creditCards, cc => (
                    <tr key={cc.id}>
                        <S.TD>
                            {cc.brand} ...{cc.last4Digits} {cc.expirationMonth}/{cc.expirationYear}
                        </S.TD>
                    </tr>
                ));
                const paymentPlanRows = _.map(paymentPlans, paymentPlan => {
                    const { id, amountPerCycle, intervalCount, intervalSpan } = paymentPlan;
                    const interval = intervalCount && intervalCount > 1 ? intervalCount : '';
                    const suffix = intervalCount && intervalCount > 1 ? 's' : '';
                    return (
                        <tr key={id}>
                            <S.TD>
                                ${_.get(amountPerCycle, 'amount')} payment every {interval} {_.toLower(intervalSpan)}
                                {suffix}
                                <br />
                            </S.TD>
                        </tr>
                    );
                });
                return (
                    <S.Table>
                        <thead>
                            <tr>
                                <td colSpan={4}>
                                    <S.Title>Payment</S.Title>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            {creditCardRows}
                            {paymentPlanRows}
                            {_.isEmpty(creditCardRows) && _.isEmpty(paymentPlanRows) && (
                                <tr>
                                    <td colSpan={4} align="center">
                                        No payments
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </S.Table>
                );
            };

            const renderCharges = (): React.ReactElement => {
                const chargeRows = _.map(checkoutCharges, checkoutCharge => (
                    <tr key={checkoutCharge.id}>
                        <S.TD nowrap={true}>{checkoutCharge.chargeDate}</S.TD>
                        <S.TD>${_.get(checkoutCharge, 'amount.amount')}</S.TD>
                        <S.TD>${_.get(checkoutCharge, 'feeAmount.amount')}</S.TD>
                        <S.TD>{checkoutCharge.status}</S.TD>
                        <S.TD>
                            {checkoutCharge.status !== CheckoutChargeStatusType.REFUNDED && (
                                <S.StyledButton variant="text" onClick={refundCharge(checkoutCharge)}>
                                    Refund
                                </S.StyledButton>
                            )}
                        </S.TD>
                    </tr>
                ));
                return (
                    <S.Table>
                        <thead>
                            <tr>
                                <td colSpan={5}>
                                    <S.Title>Charges</S.Title>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <S.TH nowrap={true}>Date</S.TH>
                                <S.TH>Amount</S.TH>
                                <S.TH nowrap={true}>Fee Amount</S.TH>
                                <S.TH>Status</S.TH>
                                <S.TH />
                            </tr>
                            {chargeRows}
                            {_.isEmpty(chargeRows) && (
                                <tr>
                                    <td colSpan={4} align="center">
                                        No charges
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </S.Table>
                );
            };
            const renderRefunds = (): React.ReactElement => {
                const checkoutRefunds = _.flatMap(checkoutCharges, 'refunds');
                const refundRows = _.map(checkoutRefunds, checkoutChargeRefund => (
                    <tr key={checkoutChargeRefund.id}>
                        <S.TD nowrap={true}>{checkoutChargeRefund.refundDate}</S.TD>
                        <S.TD>${_.get(checkoutChargeRefund, 'refundedAmount.amount')}</S.TD>
                        <S.TD>${_.get(checkoutChargeRefund, 'refundedFeeAmount.amount')}</S.TD>
                        <S.TD>{checkoutChargeRefund.memo}</S.TD>
                    </tr>
                ));
                return (
                    <S.Table>
                        <thead>
                            <tr>
                                <td colSpan={4}>
                                    <S.Title>Refunds</S.Title>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <S.TH nowrap={true}>Date</S.TH>
                                <S.TH>Amount</S.TH>
                                <S.TH nowrap={true}>Fee Amount</S.TH>
                                <S.TH>Memo</S.TH>
                            </tr>
                            {refundRows}
                            {_.isEmpty(refundRows) && (
                                <tr>
                                    <td colSpan={4} align="center">
                                        No refunds
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </S.Table>
                );
            };
            return (
                <S.GuarantorOverviewCard size="large">
                    <S.Title>Guarantor Information</S.Title>
                    {loading && renderLoader()}
                    {!loading && !guarantor && (
                        <S.Body>
                            <S.NoResults>No guarantor</S.NoResults>
                        </S.Body>
                    )}
                    {!loading && guarantor && (
                        <>
                            <S.Body>
                                {renderGuarantor()}
                                {renderPayments()}
                            </S.Body>
                            <S.Body>
                                {renderCharges()}
                                {renderRefunds()}
                            </S.Body>
                        </>
                    )}
                </S.GuarantorOverviewCard>
            );
        },
    ),
);
